<script setup>
import {ref, computed, nextTick, onBeforeUnmount} from 'vue';
import {useRouter, useRoute} from 'vue-router';
import {useAuthService} from '~/composables/useAuthService';
import SocialLoginButtons from "~/components/auth/SocialLoginButtons.client.vue";
import AccountSetupFields from "~/components/auth/AccountSetupFields.vue";
import {dateOfBirthProblem} from "~/composables/useAccountSetup";
import {safeRedirectPath} from '~/composables/useSafeRedirect';
import {useDates} from "~/composables/useDates";
import {legalHref} from '~/composables/useLegalLinks';
import {
  REGISTER_CONFIRMATION,
  canResendRegistration,
  registerSentTo,
  resendSecondsLeft,
  resendWaitLine,
} from '~/composables/useRegisterConfirmation.js';

const router = useRouter();
const route = useRoute();

const props = defineProps({
  redirectUrl: {
    type: String,
    default: "/",
  },
  authId: {
    type: String,
    default: null,
  },
  // hides the hero block when the form is embedded in another card (invite page)
  hideHero: {
    type: Boolean,
    default: false,
  },
  // One more line under "Check your email" saying where the emailed link
  // leads (the invite page: back to the invite).
  sentNote: {
    type: String,
    default: null,
  },
});

const {base64StringToObj} = useCrudConverters();

// Data properties
const firstName = ref(null);
const lastName = ref(null);
// A link may bring the address along (a guest payer saving their card as an account, ME12).
const queryEmail = typeof route.query.email === 'string' && /^[^\s@*]+@[^\s@*]+\.[^\s@*]+$/.test(route.query.email) ? route.query.email : null;
const email = ref(queryEmail);
const telephone = ref(null);
const password = ref(null);

const {
  register,
  registerLoading,
  registerErrors,
  registerItem,
  registerVerification,
  registerVerificationLoading,
  registerVerificationErrors,
  registerVerificationItem,
  hasRegisterVerificationErrors
} = useAuthService();


// Terms are accepted as the account is created, not afterwards: that is the
// moment the contract forms. Date of birth decides who is a junior.
// Registration asks those two and nothing else. Where they play, which country
// and which competitions they enter are asked by the sign-up prompt set on the
// first screen after this one (SIGNUP_PROMPT_PLAN.md).
const setup = ref({dateOfBirth: null});
const termsAccepted = ref(false);
const {toMachineDate} = useDates();

const firstNameRef = ref(false);
const emailRef = ref(false);

const isLoading = computed(() => {
  return registerLoading.value || registerVerificationLoading.value;
});
const isVerifiedEmail = computed(() => {
  return registerVerificationItem.value && registerVerificationItem.value.email && !registerVerificationItem.value.error;
});
const canEnterProfile = computed(() => {
  return isVerifiedEmail.value;
});

const isValidEmail = (email) => {
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}
const isButtonDisabled = computed(() => {
  if (!canEnterProfile.value) {
    return !isValidEmail(email.value);
  } else {
    return !firstName.value || firstName.value.length < 1 ||
           !lastName.value || lastName.value.length < 1 ||
           !termsAccepted.value ||
           dateOfBirthProblem(setup.value.dateOfBirth) !== null;
  }
});

// "Check your email" follows the send itself (release fixes, 28 Sep 2026).
// It used to appear only when the API's answer equalled the typed address, and
// the API answers it lowercased, so a capital letter showed nothing and the
// button could be pressed again. `sending` is set before the reCAPTCHA wait,
// which is not covered by the request's own loading flag.
const sending = ref(false);
const sentTo = ref(null);
const sentAt = ref(null);
const resent = ref(false);
const lastRegisterData = ref(null);
const now = ref(Date.now());
let clock = null;
const stopClock = () => {
  if (clock) clearInterval(clock);
  clock = null;
};
const startClock = () => {
  stopClock();
  now.value = Date.now();
  if (typeof window === 'undefined') return;
  clock = setInterval(() => {
    now.value = Date.now();
    if (canResendRegistration(sentAt.value, now.value)) stopClock();
  }, 1000);
};
onBeforeUnmount(stopClock);
const canResend = computed(() => canResendRegistration(sentAt.value, now.value));
const resendLine = computed(() => resendWaitLine(resendSecondsLeft(sentAt.value, now.value)));

const sendRegistration = async (registerData) => {
  // register() adds the reCAPTCHA answer to what it is given, so send a copy.
  await register({...registerData});
  const to = registerSentTo(registerItem.value, registerErrors.value, registerData.email);
  if (!to) return false;
  sentTo.value = to;
  sentAt.value = Date.now();
  startClock();
  return true;
};

const resend = async () => {
  if (sending.value || !canResend.value || !lastRegisterData.value) return;
  sending.value = true;
  resent.value = false;
  try {
    resent.value = await sendRegistration(lastRegisterData.value);
  } finally {
    sending.value = false;
  }
};

const formAction = async () => {
  if (sending.value) return;
  sending.value = true;
  try {
    await submitStep();
  } finally {
    sending.value = false;
  }
};

const submitStep = async () => {
  if (isVerifiedEmail.value) {
    const registerData = {
      firstName : firstName.value,
      lastName : lastName.value,
      email : email.value,
      telephone : telephone.value,
      dateOfBirth : toMachineDate(setup.value.dateOfBirth),
      termsAccepted : termsAccepted.value,
    };
    if (props.authId) {
      registerData.authId = props.authId;
    }
    // Round-trip the return-to through the emailed link (the server keeps a
    // site-relative path only), so the account lands where the link pointed.
    if (safeRedirectPath(props.redirectUrl) !== '/') {
      registerData.redirectUrl = props.redirectUrl;
    }
    lastRegisterData.value = registerData;
    if (await sendRegistration(registerData)) {
      // Scroll back to the top so a phone user, who pressed the button at the
      // foot of the form, actually sees the confirmation.
      await nextTick();
      if (typeof window !== 'undefined') {
        window.scrollTo({top: 0, behavior: 'smooth'});
      }
    }
  } else {
    await registerVerification({email: email.value});
    if (isVerifiedEmail.value) {
      firstNameRef.value?.focus();
    }
  }
};

const verificationErrors = computed(() => {
  if(registerVerificationItem.value && registerVerificationItem.value.error) {
    return {
      global: [registerVerificationItem.value.error]
    }
  }
  return {};
});
const changeVerifiedEmail = () => {
  stopClock();
  sentTo.value = null;
  sentAt.value = null;
  resent.value = false;
  lastRegisterData.value = null;
  registerItem.value = null;
  email.value = null;
  registerVerificationItem.value = null;
  emailRef.value?.focus();
}


</script>
<template>
  <!-- "Check your email" state (register-verify.html) -->
  <div v-if="sentTo">
    <div class="auth-hero">
      <div class="auth-icon-tile brand">
        <v-icon size="30" icon="mdi-email-outline"/>
      </div>
      <h1>{{ REGISTER_CONFIRMATION.title }}</h1>
      <p style="word-break: break-word;">
        {{ REGISTER_CONFIRMATION.sentTo }} <b class="email-emphasis">{{ sentTo }}</b>.
        {{ REGISTER_CONFIRMATION.nextStep }}
        <template v-if="sentNote">{{ sentNote }}</template>
      </p>
    </div>
    <crud-error-display class="mt-3" :errors="registerErrors" show-all/>
    <div v-if="resent" class="auth-help mt-3">{{ REGISTER_CONFIRMATION.resent }}</div>
    <v-btn v-if="canResend" size="large" color="primary" variant="flat" class="mt-3" :block="true"
           :loading="sending" :disabled="sending" @click="resend">
      {{ REGISTER_CONFIRMATION.resend }}
    </v-btn>
    <div v-else class="auth-help mt-3">{{ resendLine }}</div>
    <v-btn size="large" variant="outlined" class="auth-social-btn mt-3" :block="true" :disabled="sending"
           @click="changeVerifiedEmail">
      {{ REGISTER_CONFIRMATION.differentEmail }}
    </v-btn>
    <div class="auth-foot">{{ REGISTER_CONFIRMATION.spam }}</div>
  </div>

  <div v-else>
    <!-- Two-step registration (mirrors the sign-in flow): step 1 checks the email via the
         EXISTING api/v2/auth/register/verification call; step 2 locks it and collects the
         same profile fields as before (first/last name, optional telephone). -->
    <div v-if="!hideHero" class="auth-hero mb-2">
      <div class="auth-logo"><img src="/images/icon_red.png" alt="Tap Leagues"/></div>
      <h1>Create your account</h1>
      <p>Join your club's leagues, ladders and tournaments in a couple of minutes.</p>
    </div>

    <social-login-buttons type="register" :redirect-url="redirectUrl" :auth-id="authId"/>

    <div class="auth-divider">or sign up with email</div>

    <v-form @submit.prevent="formAction">
      <!-- Step 2: email checked and locked -->
      <template v-if="isVerifiedEmail">
        <div class="tl-label mb-1">Email address</div>
        <div class="auth-locked-row">
          <v-icon size="18" color="success" icon="mdi-check-circle"/>
          <span class="email">{{ registerVerificationItem.email }}</span>
          <a class="change" @click="changeVerifiedEmail">Change</a>
        </div>
        <div class="auth-help mb-4">Email checked. No existing account, so you're good to go.</div>
      </template>

      <!-- Step 1: email entry -->
      <template v-else>
        <div class="tl-label mb-1">Email address</div>
        <v-text-field
            ref="emailRef"
            autocomplete="false"
            density="compact"
            type="email"
            placeholder="you@club.com"
            prepend-inner-icon="mdi-email-outline"
            :loading="isLoading"
            :disabled="isLoading"
            v-model="email"
            :error="registerErrors.email != null"
            :error-messages="registerErrors.email"
            hide-details="auto"
        ></v-text-field>
        <div class="auth-help">We'll check your email first. Your details come next.</div>
        <crud-error-display class="mt-3" :errors="verificationErrors"/>
      </template>

      <template v-if="canEnterProfile">
        <v-row dense>
          <v-col cols="12" sm="6">
            <div class="tl-label mb-1">First name</div>
            <v-text-field
                ref="firstNameRef"
                autocomplete="false"
                density="compact"
                :loading="registerLoading"
                :disabled="registerLoading"
                v-model="firstName"
                placeholder="First name"
                :error="registerErrors.firstName != null"
                :error-messages="registerErrors.firstName"
                hide-details="auto"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6">
            <div class="tl-label mb-1">Last name</div>
            <v-text-field
                autocomplete="false"
                density="compact"
                :loading="registerLoading"
                :disabled="registerLoading"
                v-model="lastName"
                placeholder="Last name"
                :error="registerErrors.lastName != null"
                :error-messages="registerErrors.lastName"
                hide-details="auto"
            ></v-text-field>
          </v-col>
        </v-row>

        <div class="tl-label mb-1 mt-4">Telephone <span class="optional">(optional)</span></div>
        <v-text-field
            autocomplete="false"
            density="compact"
            type="tel"
            prepend-inner-icon="mdi-phone-outline"
            :loading="registerLoading"
            :disabled="registerLoading"
            v-model="telephone"
            :error="registerErrors.telephone != null"
            :error-messages="registerErrors.telephone"
            hide-details="auto"
        ></v-text-field>

        <div class="mt-4">
          <account-setup-fields
              v-model="setup"
              :disabled="registerLoading"
              :errors="registerErrors"
          />
        </div>

        <v-checkbox v-model="termsAccepted" :disabled="registerLoading" density="compact" hide-details class="mt-3">
          <template #label>
            <!-- A NEW TAB, not a route change. These sit inside a half-filled
                 sign-up form, and navigating away unmounts it: the name, email,
                 password and date of birth they had already typed are gone, and
                 coming back starts again. Reading a document you are being
                 asked to accept must not cost you the form. -->
            <span class="asf-terms">
              I have read and accept the
              <a :href="legalHref('terms')" target="_blank" rel="noopener" @click.stop>Terms of Use</a>
              and the
              <a :href="legalHref('privacy')" target="_blank" rel="noopener" @click.stop>Privacy Policy</a>.
            </span>
          </template>
        </v-checkbox>
      </template>

      <crud-error-display class="mt-3" :errors="registerErrors"/>

      <v-btn :disabled="isButtonDisabled || sending" size="large" type="submit" :block="true" class="mt-5"
             color="primary" :loading="isLoading || sending">
        {{ isVerifiedEmail ? 'Create account' : 'Continue' }}
      </v-btn>
      <div v-if="isVerifiedEmail" class="auth-note">
        We'll email you a link to set your password and finish up.
      </div>
    </v-form>

    <div v-if="!authId" class="auth-foot">
      Already have an account? <router-link :to="{path: '/login', query: $route.query}">Sign in</router-link>
    </div>
  </div>
</template>
<style scoped>
.asf-terms { font-size: 13.5px; line-height: 1.45; }
.email-emphasis {
  color: rgb(var(--v-theme-charcoal));
  font-weight: 600;
}

.optional {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
}
</style>
